import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { forkJoin } from 'rxjs';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { WarehousingMasterType, companyOwnedWarehouses, masterDataMap, warehousingBreadcrumb, warehousingHomeBreadcrumb } from '../warehousing-common';
import { WarehousingItem, WarehousingMasterData, WarehousingStockEntry } from '../warehousing.model';
import { WarehousingLabelPrintService } from '../warehousing-label-print.service';
import { WarehousingService } from '../warehousing.service';
import { itemIdentityLabel, itemSizeLabel } from '../warehousing-item-display';

@Component({
  selector: 'app-warehousing-stock-entry-scan',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, RouterModule, BreadcrumbModule, ButtonModule, CardModule, DatePickerModule,
    FormsModule, IftaLabelModule, InputGroupModule, InputGroupAddonModule, InputNumberModule, InputTextModule,
    ProgressBarModule, SelectModule, TextareaModule, ToastModule, PageFormSectionComponent, PageHeroComponent,
    PageShellComponent
  ],
  providers: [MessageService],
  templateUrl: './warehousing-stock-entry-scan.component.html'
})
export class WarehousingStockEntryScanComponent implements OnInit {
  readonly itemSizeLabel = itemSizeLabel;
  private fb = inject(FormBuilder);
  private warehousingService = inject(WarehousingService);
  private labelPrintService = inject(WarehousingLabelPrintService);
  private messageService = inject(MessageService);

  isLoading = false;
  isResolvingBarcode = false;
  isSubmitting = false;
  isPrinting = false;
  resolvedItem?: WarehousingItem;
  lastReceipt?: WarehousingStockEntry;
  barcodeValue = '';
  masterData = masterDataMap();
  breadcrumbItems: MenuItem[] = warehousingBreadcrumb('Scan Receive', 'Receive Stock', '/warehousing/stock-entry');
  homeBreadcrumb: MenuItem = warehousingHomeBreadcrumb();

  scanForm = this.fb.group({
    referenceNumber: [''],
    entryDate: [this.today(), Validators.required],
    regionId: [null as number | null, Validators.required],
    warehouseId: [null as number | null, Validators.required],
    zoneId: [null as number | null, Validators.required],
    conditionId: [null as number | null, Validators.required],
    notes: [''],
    uniqueQuantity: [1, [Validators.required, Validators.min(1)]]
  });

  handlingUnits: FormArray<FormGroup> = this.fb.array<FormGroup>([]);

  ngOnInit(): void {
    this.scanForm.get('regionId')?.valueChanges.subscribe(() => this.reconcilePlacementHierarchy());
    this.scanForm.get('warehouseId')?.valueChanges.subscribe(() => this.reconcilePlacementHierarchy());
    this.loadMasterData();
  }

  loadMasterData(): void {
    this.isLoading = true;
    forkJoin({
      regions: this.warehousingService.getMasterData('REGION'),
      warehouses: this.warehousingService.getMasterData('WAREHOUSE'),
      zones: this.warehousingService.getMasterData('ZONE'),
      conditions: this.warehousingService.getMasterData('ITEM_CONDITION')
    }).subscribe({
      next: response => {
        this.masterData.REGION = response.regions.data || [];
        this.masterData.WAREHOUSE = companyOwnedWarehouses(response.warehouses.data || []);
        this.masterData.ZONE = response.zones.data || [];
        this.masterData.ITEM_CONDITION = response.conditions.data || [];
        this.reconcilePlacementHierarchy();
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  resolveBarcode(): void {
    const barcode = (this.barcodeValue || '').trim();
    if (!barcode) {
      return;
    }
    this.isResolvingBarcode = true;
    this.warehousingService.getItemByBarcode(barcode).subscribe({
      next: response => {
        this.resolvedItem = response.data;
        this.lastReceipt = undefined;
        this.resetHandlingUnits();
        if (!this.isUniqueTracked()) {
          this.addHandlingUnit(1, 1);
        } else {
          this.scanForm.patchValue({ uniqueQuantity: 1 });
        }
        this.messageService.add({
          severity: 'success',
          summary: 'Item Resolved',
          detail: itemIdentityLabel(this.resolvedItem, 'Scanned item loaded.')
        });
        this.isResolvingBarcode = false;
      },
      error: () => this.isResolvingBarcode = false
    });
  }

  addHandlingUnit(quantity = 1, copies = 1, unitCode = ''): void {
    this.handlingUnits.push(this.fb.group({
      quantity: [quantity, [Validators.required, Validators.min(0.0001)]],
      copies: [copies, [Validators.required, Validators.min(1)]],
      unitCode: [unitCode]
    }));
  }

  removeHandlingUnit(index: number): void {
    this.handlingUnits.removeAt(index);
  }

  receiveStock(): void {
    if (this.isSubmitting) {
      return;
    }
    if (!this.resolvedItem) {
      this.messageService.add({ severity: 'warn', summary: 'Scan Required', detail: 'Scan an item barcode first.' });
      return;
    }
    if (this.scanForm.invalid) {
      this.scanForm.markAllAsTouched();
      return;
    }
    if (!this.isUniqueTracked() && this.handlingUnits.length === 0) {
      this.messageService.add({ severity: 'warn', summary: 'Handling Units Required', detail: 'Add at least one handling unit.' });
      return;
    }

    const payload = this.buildStockEntryPayload();
    this.isSubmitting = true;
    this.warehousingService.createStockEntry(payload).subscribe({
      next: response => {
        this.lastReceipt = response.data;
        this.messageService.add({
          severity: 'success',
          summary: 'Stock Received',
          detail: response.data?.entryNumber ? `${response.data.entryNumber} was received.` : 'Stock was received.'
        });
        this.isSubmitting = false;
      },
      error: () => this.isSubmitting = false
    });
  }

  async printLabels(): Promise<void> {
    const stockUnitIds = (this.lastReceipt?.stockUnits || []).map(unit => unit.id).filter(Boolean) as number[];
    if (!stockUnitIds.length) {
      this.messageService.add({ severity: 'warn', summary: 'No Labels', detail: 'No stock units are available to print.' });
      return;
    }
    this.isPrinting = true;
    this.warehousingService.printStockUnitLabels({ stockUnitIds, template: 'WAREHOUSE_STOCK_4X6' }).subscribe({
      next: async blob => {
        const mode = await this.labelPrintService.printOrDownload(blob, `${this.lastReceipt?.entryNumber || 'warehouse-stock-labels'}.zpl`);
        this.messageService.add({
          severity: 'success',
          summary: mode === 'printed' ? 'Labels Printed' : 'Labels Downloaded',
          detail: mode === 'printed' ? 'Labels were sent to the default Zebra printer.' : 'ZPL labels were downloaded.'
        });
        this.isPrinting = false;
      },
      error: () => this.isPrinting = false
    });
  }

  warehouseOptions(): WarehousingMasterData[] {
    const regionId = this.scanForm.get('regionId')?.value as number | null;
    return regionId ? this.masterData.WAREHOUSE.filter(row => row.parentId === regionId) : [];
  }

  zoneOptions(): WarehousingMasterData[] {
    const warehouseId = this.scanForm.get('warehouseId')?.value as number | null;
    return warehouseId ? this.masterData.ZONE.filter(row => row.parentId === warehouseId) : [];
  }

  options(type: WarehousingMasterType): WarehousingMasterData[] {
    return this.masterData[type] || [];
  }

  handlingUnitRows(): FormGroup[] {
    return this.handlingUnits.controls;
  }

  totalQuantity(): number {
    if (this.isUniqueTracked()) {
      return Number(this.scanForm.get('uniqueQuantity')?.value || 0);
    }
    return this.handlingUnits.controls.reduce((sum, control) => {
      const quantity = Number(control.get('quantity')?.value || 0);
      const copies = Number(control.get('copies')?.value || 0);
      return sum + (quantity * copies);
    }, 0);
  }

  isUniqueTracked(): boolean {
    return !!this.resolvedItem && (this.resolvedItem.isDisplayStand || this.resolvedItem.trackingMode === 'UNIT' || this.resolvedItem.trackingMode === 'UNIQUE_ASSET');
  }

  private buildStockEntryPayload(): WarehousingStockEntry {
    const value = this.scanForm.getRawValue();
    return {
      referenceNumber: value.referenceNumber || undefined,
      crmClientId: this.resolvedItem!.crmClientId,
      crmAccountId: this.resolvedItem!.crmAccountId,
      itemId: this.resolvedItem!.id!,
      regionId: value.regionId!,
      warehouseId: value.warehouseId!,
      zoneId: value.zoneId!,
      conditionId: value.conditionId!,
      skuNumber: this.resolvedItem?.skuNumber || undefined,
      customerSkuNumber: this.resolvedItem?.customerItemNumber || undefined,
      quantity: this.totalQuantity(),
      entryDate: value.entryDate!,
      notes: value.notes || undefined,
      handlingUnits: this.isUniqueTracked() ? undefined : this.handlingUnits.controls.map(control => ({
        quantity: Number(control.get('quantity')?.value || 0),
        copies: Number(control.get('copies')?.value || 1),
        unitCode: control.get('unitCode')?.value || undefined
      }))
    };
  }

  private resetHandlingUnits(): void {
    while (this.handlingUnits.length) {
      this.handlingUnits.removeAt(0);
    }
  }

  private reconcilePlacementHierarchy(): void {
    const regionId = this.scanForm.get('regionId')?.value as number | null;
    const warehouseId = this.scanForm.get('warehouseId')?.value as number | null;
    const zoneId = this.scanForm.get('zoneId')?.value as number | null;

    const warehouseStillValid = !!warehouseId
      && this.masterData.WAREHOUSE.some(row => row.id === warehouseId && row.parentId === regionId);
    if (warehouseId && !warehouseStillValid) {
      this.scanForm.patchValue({ warehouseId: null, zoneId: null }, { emitEvent: false });
      return;
    }

    const zoneStillValid = !!zoneId
      && this.masterData.ZONE.some(row => row.id === zoneId && row.parentId === warehouseId);
    if (zoneId && !zoneStillValid) {
      this.scanForm.patchValue({ zoneId: null }, { emitEvent: false });
    }
  }

  private today(): Date {
    return new Date();
  }
}
